import React from "react";
import mainLogo from "../../../Image/Main_logo.png";
import "./PartyCallPrintReport.css";

const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const DAYS_OF_WEEK = [
  { name: "SUNDAY", short: "Sun", isWeekend: true },
  { name: "MONDAY", short: "Mon", isWeekend: false },
  { name: "TUESDAY", short: "Tue", isWeekend: false },
  { name: "WEDNESDAY", short: "Wed", isWeekend: false },
  { name: "THURSDAY", short: "Thu", isWeekend: false },
  { name: "FRIDAY", short: "Fri", isWeekend: false },
  { name: "SATURDAY", short: "Sat", isWeekend: false },
];

const formatDate = (dateString) => {
  if (!dateString) return "N/A";
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return "N/A";
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
};

const formatTime = (dateString) => {
  if (!dateString) return "N/A";
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return "N/A";
  let hours = date.getHours();
  const minutes = String(date.getMinutes()).padStart(2, "0");
  const ampm = hours >= 12 ? "PM" : "AM";
  hours = hours % 12 || 12;
  return `${hours}:${minutes} ${ampm}`;
};

const getStatusBadge = (status) => {
  const s = (status || "").toLowerCase().trim();
  if (s === "completed") {
    return { label: "COMPLETED", className: "badge-completed" };
  }
  if (s === "pending") {
    return { label: "PENDING", className: "badge-pending" };
  }
  if (s === "accept" || s === "accepted") {
    return { label: "ACCEPT", className: "badge-accept" };
  }
  if (s === "forward" || s === "forwarded") {
    return { label: "FORWARDED", className: "badge-forward" };
  }
  if (s === "under review" || s === "review") {
    return { label: "UNDER REVIEW", className: "badge-review" };
  }
  return { label: (status || "ACTIVE").toUpperCase(), className: "badge-other" };
};

const PartyCallMonthCalendarSheet = ({
  id = null,
  party,
  month,
  year,
  selectedProjectName = null,
  calendarWeeks = [],
  monthStats = { total: 0, completed: 0, pending: 0, other: 0 },
  pageNumber = 1,
  totalPages = null,
  isLastPage = false,
  isScreenPreview = false,
  nowFormatted = null,
}) => {
  const partyName = party?.CompanyName || "All Parties";
  const partyType = party?.Type || "CLIENT";
  const monthName = MONTH_NAMES[month] || "";

  const timeStamp = React.useMemo(() => {
    if (nowFormatted) return nowFormatted;
    const d = new Date();
    return `${formatDate(d)} at ${formatTime(d)}`;
  }, [nowFormatted]);

  const resolutionRate =
    monthStats.total > 0
      ? `${Math.round((monthStats.completed / monthStats.total) * 100)}%`
      : "100%";

  const is5Weeks = calendarWeeks.length <= 5;

  return (
    <div
      id={id || `calendar-sheet-page-${pageNumber}`}
      className={`print-page-1 ${isLastPage ? "is-last-page" : ""} ${
        isScreenPreview ? "screen-preview-sheet" : ""
      }`}
    >
      {isScreenPreview && (
        <div className="screen-page-tag">
          {totalPages
            ? `Page ${pageNumber} of ${totalPages} • ${monthName} ${year} Calendar & Monthly Summary`
            : `Page ${pageNumber}: Complete Calendar & Monthly Summary (${monthName} ${year})`}
        </div>
      )}

      {/* Report Header */}
      <div className="print-header">
        <div className="print-header-brand">
          <img
            src={mainLogo}
            alt="Dynamic Softlink"
            className="print-header-logo"
          />
          <div className="print-header-titles">
            <h1>DYNAMIC SOFTLINK &ndash; PARTY CALL REPORT</h1>
            <p>Monthly Support &amp; Activity Comprehensive Statement</p>
          </div>
        </div>

        <div className="print-header-meta">
          <div className="party-name">
            {partyName}
            <span className="meta-pill">{partyType}</span>
          </div>
          <div>
            <strong>Period:</strong> {monthName} {year}
            {selectedProjectName && (
              <>
                {" "}
                &bull; <strong>Project:</strong> {selectedProjectName}
              </>
            )}
          </div>
          <div style={{ fontSize: "8px", color: "#64748b", marginTop: "1px" }}>
            Report Generated: {timeStamp}
          </div>
        </div>
      </div>

      {/* Calendar Month Navigation & Status Summary Bar */}
      <div className="print-month-bar">
        <div className="print-month-title">
          {monthName} {year}
        </div>
        <div className="print-legend">
          <div className="print-legend-item">
            <span>Completed</span>
            <span className="print-legend-badge badge-completed">
              {monthStats.completed}
            </span>
          </div>
          <div className="print-legend-item">
            <span>Under Review</span>
            <span className="print-legend-badge badge-review">
              {monthStats.other || 0}
            </span>
          </div>
          <div className="print-legend-item">
            <span>Pending</span>
            <span className="print-legend-badge badge-pending">
              {monthStats.pending}
            </span>
          </div>
        </div>
      </div>

      {/* Complete 7-Column Calendar Grid */}
      <table className="print-calendar-table">
        <thead>
          <tr>
            {DAYS_OF_WEEK.map((d, idx) => (
              <th
                key={idx}
                className={`print-calendar-th ${d.isWeekend ? "th-sunday" : ""}`}
              >
                {d.name}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {calendarWeeks.map((week, wIdx) => (
            <tr key={wIdx}>
              {week.days.map((dayObj, dIdx) => {
                const isSunday = dIdx === 0;
                const hasCalls = dayObj.calls && dayObj.calls.length > 0;
                const dayCalls = dayObj.calls || [];

                return (
                  <td
                    key={dIdx}
                    className={`print-calendar-cell ${
                      is5Weeks ? "cell-5weeks" : ""
                    } ${
                      !dayObj.isCurrentMonth
                        ? "other-month"
                        : hasCalls
                        ? "has-calls"
                        : ""
                    }`}
                  >
                    <div className="print-cell-header">
                      <span
                        className={`print-day-num ${
                          !dayObj.isCurrentMonth
                            ? "muted-num"
                            : isSunday
                            ? "sunday-num"
                            : ""
                        }`}
                      >
                        {dayObj.dayNumber}
                      </span>

                      {dayObj.isCurrentMonth && hasCalls && (
                        <span className="print-call-count-tag">
                          {dayCalls.length}{" "}
                          {dayCalls.length === 1 ? "call" : "calls"}
                        </span>
                      )}
                    </div>

                    {/* Stack of compact cards for calls on this date */}
                    {dayObj.isCurrentMonth && hasCalls && (
                      <div className="print-cards-stack">
                        {dayCalls.slice(0, 2).map((c, cIdx) => {
                          const badge = getStatusBadge(c.Status);
                          const projectName =
                            c.Project?.project_name ||
                            c.Project?.ProjectName ||
                            (typeof c.Project === "string" ? c.Project : "");
                          const reqText =
                            (c.Requirement || "").trim() ||
                            (c.PresentStatus || "").trim() ||
                            "Support Request";

                          const staffName =
                            c.SuportTeamId?.Name ||
                            c.SuportTeamId?.OwnerName ||
                            (typeof c.SuportTeamId === "string" ? c.SuportTeamId : "") ||
                            c.SeniorDevloperID?.Name ||
                            c.SeniorDevloperID?.OwnerName ||
                            c.Solve_By?.Name ||
                            c.Complete_By?.Name ||
                            "";

                          const s = (c.Status || "").toLowerCase();
                          const cardBorderClass =
                            s === "completed"
                              ? "card-completed"
                              : s === "pending"
                              ? "card-pending"
                              : "card-review";

                          return (
                            <div
                              key={c._id || cIdx}
                              className={`print-card-compact ${cardBorderClass}`}
                            >
                              <div className="print-card-top-row">
                                <span className="print-card-callno">
                                  #{c.CallNo || "N/A"}
                                </span>
                                {projectName && (
                                  <span
                                    className="print-card-project"
                                    title={projectName}
                                  >
                                    {projectName}
                                  </span>
                                )}
                                <span
                                  className={`print-card-status-badge ${badge.className}`}
                                >
                                  {badge.label}
                                </span>
                              </div>
                              <div
                                className="print-card-req"
                                title={reqText}
                              >
                                {reqText}
                              </div>
                              {staffName && (
                                <div className="print-card-staff" title={staffName}>
                                  {s === "completed" ? "✓ " : "• "}{staffName}
                                </div>
                              )}
                            </div>
                          );
                        })}

                        {dayCalls.length > 2 && (
                          <div
                            style={{
                              fontSize: "7px",
                              color: "#154D75",
                              fontWeight: 800,
                              textAlign: "center",
                              lineHeight: "1",
                            }}
                          >
                            + {dayCalls.length - 2} more
                          </div>
                        )}
                      </div>
                    )}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>

      {/* Monthly Summary Section - Complete & Sits strictly on this page */}
      <div className="print-monthly-summary">
        <div className="print-summary-header">
          <div>
            <span className="print-summary-title">
              Monthly Summary &ndash; {monthName} {year}
            </span>
            <span className="print-summary-subtitle" style={{ marginLeft: "6px" }}>
              &bull; Call statistics for {partyName}
            </span>
          </div>
          <div className="print-resolution-badge">
            Resolution Rate: {resolutionRate}
          </div>
        </div>

        <div className="print-summary-grid">
          <div className="print-summary-card card-total">
            <span className="label">Total Calls</span>
            <span className="val">{monthStats.total}</span>
          </div>
          <div className="print-summary-card card-comp">
            <span className="label">Completed</span>
            <span className="val">{monthStats.completed}</span>
          </div>
          <div className="print-summary-card card-prog">
            <span className="label">In Progress</span>
            <span className="val">{monthStats.other || 0}</span>
          </div>
          <div className="print-summary-card card-pend">
            <span className="label">Pending</span>
            <span className="val">{monthStats.pending}</span>
          </div>
        </div>
      </div>

      {/* Page Footer */}
      <div className="print-page-footer">
        <span>Dynamic Softlink ERP Customer Support System</span>
        <span>
          Page {pageNumber}
          {totalPages ? ` of ${totalPages}` : ""}{" "}
          &bull; {monthName} Calendar Overview &amp; Summary
        </span>
      </div>
    </div>
  );
};

export default PartyCallMonthCalendarSheet;
